/**
 * syncService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Responsável por sincronizar dados locais (pendentes) com o Supabase após
 * o usuário se autenticar. Garante que estudos feitos offline ou antes do
 * auth estar pronto sejam enviados ao Supabase sem duplicatas.
 */
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";

// ─── Chaves localStorage auditadas ────────────────────────────────────────────
export const LOCAL_KEYS_USER_DATA = [
  "tef_study_time_sessions",
  "tef_pending_study_sessions",
  "tef_activity_streak",
  "tef_user_goals_v2",
  "tef_test_history",
  "tef_writing_errors",
  "tef_custom_flashcards",
  "tef_custom_vocabulary",
];

const MIGRATION_FLAG_STUDY = "study_time_migrated_to_supabase";
const MIGRATION_FLAG_STREAK = "streak_migrated_to_supabase";

// ─── Sync de sessões pendentes (criadas enquanto offline / sem auth) ───────────
export async function syncPendingStudySessions(userId: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase || !userId || userId === "local_user") return;

  const raw = localStorage.getItem("tef_pending_study_sessions");
  if (!raw) return;

  let pending: any[] = [];
  try {
    pending = JSON.parse(raw);
  } catch {
    return;
  }
  if (!pending.length) return;

  console.log("[SYNC_WRITE_STUDY_SESSION] Flushing pending sessions to Supabase", {
    userId,
    count: pending.length,
  });

  const stillPending: any[] = [];

  for (const session of pending) {
    try {
      const { error } = await supabase.from("study_time_sessions").insert({
        id: session.id,
        user_id: userId,
        activity_type: session.activityType,
        competency: session.competency,
        section: session.section ?? null,
        started_at: session.startedAt,
        ended_at: session.endedAt ?? null,
        duration_seconds: session.durationSeconds ?? 0,
        metadata: session.metadata ?? {},
      });

      if (error) {
        // Unique constraint → já existe, ignorar
        if (error.code === "23505") {
          console.warn("[SYNC_WRITE_STUDY_SESSION] Duplicate, skipping", session.id);
        } else {
          console.warn("[SYNC_WRITE_STUDY_SESSION] Failed to sync, keeping in queue", error.message);
          stillPending.push(session);
        }
      } else {
        console.log("[SYNC_WRITE_STUDY_SESSION] Synced session", session.id);
      }
    } catch (err: any) {
      console.warn("[SYNC_WRITE_STUDY_SESSION] Exception, keeping in queue", err?.message);
      stillPending.push(session);
    }
  }

  if (stillPending.length > 0) {
    localStorage.setItem("tef_pending_study_sessions", JSON.stringify(stillPending));
  } else {
    localStorage.removeItem("tef_pending_study_sessions");
  }
}

// ─── Migração única: tef_study_time_sessions (localStorage legado) → Supabase ─
export async function migrateLocalStudyTimeToSupabase(userId: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase || !userId || userId === "local_user") return;

  // Só executa uma vez por usuário
  const flagKey = `${MIGRATION_FLAG_STUDY}_${userId}`;
  if (localStorage.getItem(flagKey) === "true") return;

  const raw = localStorage.getItem("tef_study_time_sessions");
  if (!raw) {
    localStorage.setItem(flagKey, "true");
    return;
  }

  let sessions: any[] = [];
  try {
    sessions = JSON.parse(raw);
  } catch {
    localStorage.setItem(flagKey, "true");
    return;
  }

  // Filtrar apenas sessões locais (userId local_user ou vazio, ou do próprio user)
  const toMigrate = sessions.filter(
    (s) => !s.userId || s.userId === "local_user" || s.userId === userId
  );
  if (!toMigrate.length) {
    localStorage.setItem(flagKey, "true");
    return;
  }

  console.log("[SYNC_AUDIT_LOCAL_KEYS] Migrando study_time_sessions locais para Supabase", {
    userId,
    count: toMigrate.length,
  });

  let migratedCount = 0;
  let skippedCount = 0;

  for (const session of toMigrate) {
    try {
      const { error } = await supabase.from("study_time_sessions").insert({
        id: session.id,
        user_id: userId,
        activity_type: session.activityType || "general",
        competency: session.competency || "general",
        section: session.section ?? null,
        started_at: session.startedAt,
        ended_at: session.endedAt ?? null,
        duration_seconds: session.durationSeconds ?? 0,
        metadata: session.metadata ?? {},
      });

      if (error) {
        if (error.code === "23505") {
          skippedCount++;
        } else {
          console.warn("[SYNC_AUDIT_LOCAL_KEYS] Falha na migração de sessão:", error.message);
        }
      } else {
        migratedCount++;
      }
    } catch (err: any) {
      console.warn("[SYNC_AUDIT_LOCAL_KEYS] Exception na migração:", err?.message);
    }
  }

  console.log("[SYNC_AUDIT_LOCAL_KEYS] Migração concluída", { migratedCount, skippedCount });
  localStorage.setItem(flagKey, "true");
}

// ─── Migração única: tef_activity_streak (localStorage) → user_streaks Supabase
export async function migrateLocalStreakToSupabase(userId: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase || !userId || userId === "local_user") return;

  const flagKey = `${MIGRATION_FLAG_STREAK}_${userId}`;
  if (localStorage.getItem(flagKey) === "true") return;

  const raw = localStorage.getItem("tef_activity_streak");
  if (!raw) {
    localStorage.setItem(flagKey, "true");
    return;
  }

  let localStreak: any = {};
  try {
    localStreak = JSON.parse(raw);
  } catch {
    localStorage.setItem(flagKey, "true");
    return;
  }

  const currentStreak = Number(localStreak.currentStreak ?? 0);
  const lastActivityDate = localStreak.lastActivityDate ?? null;
  const activityDates: string[] = Array.isArray(localStreak.activityDates)
    ? localStreak.activityDates
    : [];

  if (currentStreak === 0 && !lastActivityDate) {
    localStorage.setItem(flagKey, "true");
    return;
  }

  console.log("[SYNC_AUDIT_LOCAL_KEYS] Migrando streak local para Supabase", {
    userId,
    currentStreak,
    lastActivityDate,
  });

  try {
    // Verificar se já existe registro no Supabase
    const { data: existing } = await supabase
      .from("user_streaks")
      .select("id, current_streak, longest_streak, last_activity_date")
      .eq("user_id", userId)
      .maybeSingle();

    if (existing) {
      // Só atualiza se o valor local for maior
      if (currentStreak > (existing.current_streak || 0)) {
        await supabase
          .from("user_streaks")
          .update({
            current_streak: currentStreak,
            longest_streak: Math.max(currentStreak, existing.longest_streak || 0),
            last_activity_date: lastActivityDate,
            activity_dates: activityDates,
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", userId);
      }
    } else {
      await supabase.from("user_streaks").insert({
        user_id: userId,
        current_streak: currentStreak,
        longest_streak: currentStreak,
        last_activity_date: lastActivityDate,
        activity_dates: activityDates,
      });
    }

    localStorage.setItem(flagKey, "true");
    console.log("[SYNC_AUDIT_LOCAL_KEYS] Streak migrado com sucesso");
  } catch (err: any) {
    console.warn("[SYNC_AUDIT_LOCAL_KEYS] Falha ao migrar streak:", err?.message);
    // Não marcar como migrado — tentar de novo no próximo login
  }
}

// ─── Ponto de entrada pós-login ───────────────────────────────────────────────
export async function runPostLoginSync(userId: string): Promise<void> {
  if (!userId || userId === "local_user") return;

  console.log("[SYNC_AUDIT_LOCAL_KEYS] Iniciando sincronização pós-login", {
    userId,
    localKeys: LOCAL_KEYS_USER_DATA.filter((k) => localStorage.getItem(k) !== null),
  });

  try {
    // 1. Sessões pendentes (criadas sem auth)
    await syncPendingStudySessions(userId);
  } catch (err) {
    console.warn("[SYNC_WRITE_STUDY_SESSION] Erro ao sincronizar sessões pendentes:", err);
  }

  try {
    // 2. Migração única de histórico local antigo
    await migrateLocalStudyTimeToSupabase(userId);
  } catch (err) {
    console.warn("[SYNC_AUDIT_LOCAL_KEYS] Erro ao migrar study time local:", err);
  }

  try {
    // 3. Migração única de streak local
    await migrateLocalStreakToSupabase(userId);
  } catch (err) {
    console.warn("[SYNC_AUDIT_LOCAL_KEYS] Erro ao migrar streak local:", err);
  }
}
